import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { canEditPracticeAvailability, canManageScrimmageStats } from '../utils/practiceSchedule';
import { canEditPracticePlanForClub, isClubAdminForClub } from '../utils/practicePlan';
import { soccerPath, completeProfilePath } from '../utils/paths';
import PracticePlanSection from './PracticePlanSection';
import './AvailabilityVoting.css';

const sortByName = (a, b) =>
  (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

const matchesPlayerSearch = (name, query) => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return (name || '').toLowerCase().includes(trimmed);
};

const AvailabilityVoting = ({ type, entityId }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, isAdmin } = useAuth();
  const [entity, setEntity] = useState(null);
  const [availabilities, setAvailabilities] = useState([]);
  const [clubPlayers, setClubPlayers] = useState([]);
  const [canManagePracticeAvailability, setCanManagePracticeAvailability] = useState(false);
  const [canEditPracticePlan, setCanEditPracticePlan] = useState(false);
  const [showAddPlayerForm, setShowAddPlayerForm] = useState(false);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('AVAILABLE');
  const [addPlayerSearch, setAddPlayerSearch] = useState('');
  const [votedPlayerSearch, setVotedPlayerSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [voteStatus, setVoteStatus] = useState('');
  const [hasAutoVoted, setHasAutoVoted] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null); // 'APPROVED', 'PENDING', 'REJECTED', null
  const [hasPendingPlayer, setHasPendingPlayer] = useState(false);
  const [clubName, setClubName] = useState('');
  const voteToken = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  })();
  const [magicVoteContext, setMagicVoteContext] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  const isMagicVote = type === 'practice' && !!voteToken;

  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Store redirect URL and club ID if coming from WhatsApp and not authenticated
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    
    if (source === 'whatsapp' && !user) {
      // Store the current URL for redirect after login/register
      const currentUrl = window.location.pathname + window.location.search;
      sessionStorage.setItem('votingRedirectUrl', currentUrl);
      
      // Store club ID if entity is loaded
      if (entity && entity.clubId) {
        sessionStorage.setItem('votingClubId', entity.clubId);
      }
    }
  }, [user, entity]);

  const loadMagicVoteContext = useCallback(async () => {
    if (!voteToken) return;

    try {
      setTokenLoading(true);
      const context = await api.resolvePracticeVoteToken(voteToken);
      setMagicVoteContext(context);
      setClubName(context.clubName || '');
      if (context.currentVote) {
        setVoteStatus(context.currentVote);
      }
      setEntity({
        id: context.practiceId,
        clubId: context.clubId,
        title: context.title,
        description: context.description,
        practiceDate: context.practiceDate,
        location: context.location,
        status: context.status,
      });
    } catch (err) {
      setError(err.message || 'Invalid or expired vote link');
    } finally {
      setTokenLoading(false);
    }
  }, [voteToken]);

  const loadEntity = useCallback(async () => {
    if (isMagicVote) {
      return;
    }

    try {
      const data = type === 'match' 
        ? await api.getMatchById(entityId)
        : await api.getPracticeById(entityId, user?.id);
      setEntity(data);
      
      // Store club ID if coming from WhatsApp voting link
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source');
      if (source === 'whatsapp' && data.clubId) {
        sessionStorage.setItem('votingClubId', data.clubId);
      }
      // Load club name for pending-approval messages
      if (data.clubId) {
        try {
          const club = await api.getClubById(data.clubId);
          setClubName(club.name || '');
        } catch {
          setClubName('');
        }
      } else {
        setClubName('');
      }
    } catch (err) {
      setError(err.message || 'Failed to load details');
    }
  }, [type, entityId, isMagicVote, user?.id]);

  const loadAvailability = useCallback(async () => {
    try {
      const data = type === 'match'
        ? await api.getMatchAvailability(entityId, user?.id)
        : await api.getPracticeAvailability(entityId, user?.id);
      
      // Find current user's vote
      const userVote = data.find(a => {
        // Need to match by playerId - would need playerId from user context
        // For now, we'll check all and let user vote
        return false; // Placeholder
      });
      
      if (userVote) {
        setVoteStatus(userVote.status);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    }
  }, [type, entityId, user?.id]);

  const loadAllAvailabilities = useCallback(async () => {
    try {
      const data = type === 'match'
        ? await api.getMatchAvailability(entityId, user?.id)
        : await api.getPracticeAvailability(entityId, user?.id);
      setAvailabilities(data);
    } catch (err) {
      console.error('Failed to load availabilities:', err);
    }
  }, [type, entityId, user?.id]);

  const handleMagicVote = useCallback(async (status, isAutoVote = false) => {
    if (!voteToken || !magicVoteContext) return;

    if (!magicVoteContext.canVote) {
      setError('Cannot edit availability after the practice session has ended');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.votePracticeWithToken(voteToken, { status, notes: '' });
      setVoteStatus(status);
      setMagicVoteContext((prev) => (prev ? { ...prev, currentVote: status } : prev));
      setSuccess(isAutoVote ? 'Vote submitted automatically!' : 'Vote submitted successfully!');
      loadAllAvailabilities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (!isAutoVote) {
        setError(err.message || 'Failed to submit vote');
      }
    } finally {
      setLoading(false);
    }
  }, [voteToken, magicVoteContext, loadAllAvailabilities]);

  const checkPendingPlayer = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setHasPendingPlayer(false);
      return;
    }

    try {
      // First check if they have an approved player
      const approvedPlayers = await api.getAllPlayers(user?.id);
      const approvedPlayer = approvedPlayers.find(p => p.userId === user.id);
      
      if (!approvedPlayer) {
        // Check if there's a pending player registration
        try {
          const pendingPlayers = await api.getPendingPlayers();
          const pendingPlayer = pendingPlayers.find(p => p.userId === user.id);
          setHasPendingPlayer(!!pendingPlayer);
        } catch (err) {
          setHasPendingPlayer(false);
        }
      } else {
        setHasPendingPlayer(false);
      }
    } catch (err) {
      setHasPendingPlayer(false);
    }
  }, [isAuthenticated, user]);

  const checkMembershipStatus = useCallback(async () => {
    if (!entity || !entity.clubId || !user) return;
    
    try {
      // Get player by user email
      const allPlayers = await api.getAllPlayers(user?.id);
      const player = allPlayers.find(p => p.userId === user.id || p.email === user.email);
      
      if (player) {
        // Check club membership
        const clubMembers = await api.getClubMembers(entity.clubId, null, user?.id);
        const membership = clubMembers.find(m => m.playerId === player.id);
        
        if (membership) {
          setMembershipStatus(membership.status);
        } else {
          setMembershipStatus(null);
        }
      }
    } catch (err) {
      console.warn('Could not check membership status:', err);
    }
  }, [entity, user]);

  const loadPracticeManageAccess = useCallback(async () => {
    if (!entity || type !== 'practice' || !user) return;

    try {
      const club = await api.getClubById(entity.clubId);
      const isClubAdminUser = isClubAdminForClub(club, user);

      let myMember = null;
      let members = [];
      try {
        members = await api.getClubMembers(entity.clubId, null, user.id);
        myMember = members.find(
          (m) =>
            m.player?.userId === user.id ||
            (m.player && m.player.id && m.player.email === user.email)
        );
      } catch (memberErr) {
        console.warn('Could not load club members for role check:', memberErr);
      }

      const isPracticeCoordinator = myMember?.roles?.includes('PRACTICE_COORDINATOR');
      const canManage = isClubAdminUser || isAdmin() || isPracticeCoordinator;
      const canEditPlan = canEditPracticePlanForClub(club, user, myMember, isAdmin);

      setCanManagePracticeAvailability(canManage);
      setCanEditPracticePlan(canEditPlan);

      if (canManage && members.length > 0) {
        const players = members
          .filter((m) => m.status === 'APPROVED' && m.player)
          .map((m) => m.player);
        setClubPlayers(players);
      } else {
        setClubPlayers([]);
      }
    } catch (err) {
      console.error('Failed to check practice manage access:', err);
      setCanManagePracticeAvailability(isAdmin());
      setCanEditPracticePlan(isAdmin());
      setClubPlayers([]);
    }
  }, [entity, type, user, isAdmin]);

  const handleVote = useCallback(async (status, isAutoVote = false) => {
    if (!user || !isAuthenticated) {
      // Don't set error for unauthenticated users - login/register section will show
      return;
    }

    if (type === 'practice' && entity && !canEditPracticeAvailability(entity)) {
      setError('Cannot edit availability after the practice session has ended');
      return;
    }
    if (type === 'match' && entity && entity.status === 'COMPLETED') {
      setError('Cannot edit availability for completed matches');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const availabilityData = { status, notes: '' };
      // Get player ID from user - find player by userId
      let playerId = null;
      let player = null;
      try {
        // Try to find player by user email (since userId might not be directly linked)
        const allPlayers = await api.getAllPlayers(user?.id);
        player = allPlayers.find(p => p.userId === user.id || p.email === user.email);
        if (player) {
          playerId = player.id;
        } else {
          if (!isAutoVote) {
            setError('Player profile not found. Please register as a player first.');
          }
          setLoading(false);
          return;
        }
      } catch (err) {
        if (!isAutoVote) {
          setError('Failed to find player profile. Please register as a player first.');
        }
        setLoading(false);
        return;
      }
      
      if (!playerId) {
        if (!isAutoVote) {
          setError('Player profile not found. Please register as a player first.');
        }
        setLoading(false);
        return;
      }

      // Check club membership and approval status for practice/matches
      if (entity && entity.clubId) {
        try {
          const clubMembers = await api.getClubMembers(entity.clubId, null, user?.id);
          const userMembership = clubMembers.find(m => m.playerId === playerId);
          
          if (!userMembership) {
            if (!isAutoVote) {
              setError('You are not a member of this club. Please contact the club admin to join the club before voting.');
            }
            setLoading(false);
            return;
          }

          if (userMembership.status === 'PENDING') {
            if (!isAutoVote) {
              const namePart = clubName ? ` for ${clubName}` : '';
              setError(`Your membership${namePart} is pending approval by the club admin. You cannot vote until you are approved. Please wait for the club admin to approve your membership.`);
            }
            setLoading(false);
            return;
          }

          if (userMembership.status === 'REJECTED') {
            if (!isAutoVote) {
              setError('Your membership has been rejected by the club admin. Please contact the club admin if you believe this is an error.');
            }
            setLoading(false);
            return;
          }
        } catch (err) {
          // If we can't check membership, still allow voting (might be a permission issue)
          console.warn('Could not check club membership:', err);
        }
      }
      
      if (type === 'match') {
        await api.voteMatchAvailability(entityId, playerId, availabilityData, user?.id);
      } else {
        await api.votePracticeAvailability(entityId, playerId, availabilityData, user?.id);
      }
      
      setVoteStatus(status);
      if (isAutoVote) {
        setSuccess('Vote submitted automatically!');
      } else {
        setSuccess('Vote submitted successfully!');
      }
      loadAvailability();
      loadAllAvailabilities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (!isAutoVote) {
        // Check if error is related to club membership
        const errorMessage = err.message || 'Failed to submit vote';
        if (errorMessage.includes('member') || errorMessage.includes('approval') || errorMessage.includes('approved')) {
          setError(errorMessage);
        } else {
          setError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated, entity, type, entityId, clubName, loadAvailability, loadAllAvailabilities]);

  useEffect(() => {
    if (isMagicVote) {
      loadMagicVoteContext();
    } else {
      loadEntity();
    }
    loadAvailability();
    loadAllAvailabilities();
  }, [isMagicVote, loadMagicVoteContext, loadEntity, loadAvailability, loadAllAvailabilities]);

  // Handle deep linking from WhatsApp or magic vote links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const voteParam = urlParams.get('vote');
    const source = urlParams.get('source');
    
    // Track analytics if coming from WhatsApp
    if (source === 'whatsapp') {
      console.log('User arrived from WhatsApp');
    }
    
    const voteMap = {
      available: 'AVAILABLE',
      reserve: 'RESERVE',
      maybe: 'MAYBE',
      unavailable: 'UNAVAILABLE',
    };

    if (voteParam && voteMap[voteParam] && !hasAutoVoted && entity) {
      if (isMagicVote && magicVoteContext) {
        setHasAutoVoted(true);
        handleMagicVote(voteMap[voteParam], true);
        const tokenOnly = voteToken ? `?token=${encodeURIComponent(voteToken)}` : '';
        window.history.replaceState({}, '', window.location.pathname + tokenOnly);
        return;
      }

      if (user) {
        setHasAutoVoted(true);
        handleVote(voteMap[voteParam], true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [user, entity, magicVoteContext, isMagicVote, voteToken, hasAutoVoted, handleVote, handleMagicVote]);

  useEffect(() => {
    if (entity && type === 'practice' && user) {
      loadPracticeManageAccess();
      checkMembershipStatus();
    }
  }, [entity, type, user, loadPracticeManageAccess, checkMembershipStatus]);

  useEffect(() => {
    if (!entity || type !== 'practice') return;
    if (window.location.hash === '#practice-plan') {
      window.requestAnimationFrame(() => {
        document.getElementById('practice-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [entity, type]);

  useEffect(() => {
    if (isAuthenticated && user) {
      checkPendingPlayer();
    }
  }, [isAuthenticated, user, checkPendingPlayer]);

  const handleAddSelectedPlayers = async () => {
    if (selectedPlayerIds.length === 0) {
      setError('Please select at least one player');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    let added = 0;
    let failed = 0;

    try {
      for (const playerId of selectedPlayerIds) {
        try {
          await api.addPlayerToPracticeAvailability(entityId, playerId, user.id, {
            status: selectedStatus,
            notes: 'Added manually',
          });
          added += 1;
        } catch {
          failed += 1;
        }
      }

      if (added > 0) {
        setSuccess(
          failed > 0
            ? `Added ${added} player(s). ${failed} could not be added.`
            : `Added ${added} player(s) to practice availability.`
        );
        setSelectedPlayerIds([]);
        loadAllAvailabilities();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError('Failed to add selected players');
      }
    } finally {
      setLoading(false);
    }
  };

  const playersNotYetAdded = clubPlayers
    .filter((player) => !availabilities.some((a) => a.player?.id === player.id))
    .sort((a, b) => sortByName(a.name, b.name));

  const filteredPlayersNotYetAdded = playersNotYetAdded.filter((player) =>
    matchesPlayerSearch(player.name, addPlayerSearch)
  );

  const sortedAvailabilities = [...availabilities].sort((a, b) =>
    sortByName(a.player?.name, b.player?.name)
  );

  const filteredAvailabilities = sortedAvailabilities.filter((avail) =>
    matchesPlayerSearch(avail.player?.name, votedPlayerSearch)
  );

  const handleToggleAddPlayer = (playerId) => {
    setSelectedPlayerIds((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };

  const handleSelectAllAddPlayers = () => {
    const ids = filteredPlayersNotYetAdded.map((p) => p.id);
    const allSelected =
      ids.length > 0 && ids.every((id) => selectedPlayerIds.includes(id));
    if (allSelected) {
      setSelectedPlayerIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedPlayerIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const handleClearAddPlayerSelection = () => {
    setSelectedPlayerIds([]);
  };

  const handleUpdatePlayerVote = async (playerId, newStatus) => {
    if (!window.confirm(`Are you sure you want to update this player's vote to ${newStatus}?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.updatePlayerPracticeAvailability(entityId, playerId, user.id, {
        status: newStatus,
        notes: 'Updated manually'
      });
      setSuccess('Player vote updated successfully!');
      loadAllAvailabilities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update player vote');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayer = async (playerId, playerName) => {
    if (!window.confirm(`Are you sure you want to remove ${playerName || 'this player'} from the practice?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.removePlayerFromPractice(entityId, playerId, user.id);
      setSuccess('Player removed from practice successfully!');
      loadAllAvailabilities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to remove player');
    } finally {
      setLoading(false);
    }
  };

  const availableCount = availabilities.filter(a => a.status === 'AVAILABLE').length;
  const reserveCount = availabilities.filter(a => a.status === 'RESERVE').length;
  const unavailableCount = availabilities.filter(a => a.status === 'UNAVAILABLE').length;
  const maybeCount = availabilities.filter(a => a.status === 'MAYBE').length;
  const playerCapacity = type === 'practice'
    ? (entity?.playerCapacity ?? magicVoteContext?.playerCapacity ?? null)
    : null;
  const isAvailableFull = playerCapacity != null && playerCapacity > 0 && availableCount >= playerCapacity;
  const canSelectAvailable = !isAvailableFull || voteStatus === 'AVAILABLE';

  // Helper function to properly encode message for WhatsApp with emoji support
  const encodeWhatsAppMessage = (message) => {
    return encodeURIComponent(message);
  };

  // Get WhatsApp URL - use api.whatsapp.com for better emoji support
  const getWhatsAppUrl = (message) => {
    const encodedMessage = encodeWhatsAppMessage(message);
    return `https://api.whatsapp.com/send?text=${encodedMessage}`;
  };

  const sharePublicPracticePageViaWhatsApp = async () => {
    if (!entity || type !== 'practice' || !user?.id) return;

    try {
      setLoading(true);
      const share = await api.ensurePracticeFormationShare(entity.id, user.id);
      const token = share?.formationShareToken;
      if (!token) {
        setError('Could not create public practice page link');
        return;
      }
      const url = api.sharePublicPracticePageViaWhatsApp({
        title: entity.title,
        practiceDate: entity.practiceDate,
        location: entity.location,
        shareToken: token,
        clubName: clubName || undefined,
        teamsFormed: !!entity.teamsPublishedToPublic,
      });
      window.open(url, '_blank');
      setSuccess('Public practice page link opened in WhatsApp');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to share public practice page');
    } finally {
      setLoading(false);
    }
  };

  const shareVotedPlayersViaWhatsApp = () => {
    if (!entity || availabilities.length === 0) return;

    const entityType = type === 'practice' ? 'Practice' : 'Match';
    const entityDate = new Date(type === 'match' ? entity.matchDate : entity.practiceDate);
    const dateStr = entityDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    const timeStr = entityDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    let message = `⚽ *${entityType} Voting Status*\n\n`;
    message += `*${entity.title}*\n`;
    message += `📅 ${dateStr} at ${timeStr}\n`;
    if (entity.location) {
      message += `📍 ${entity.location}\n`;
    }
    message += `\n📊 *Voting Summary:*\n`;
    message += `✅ Available: ${availableCount}\n`;
    message += `🪑 Reserve: ${reserveCount}\n`;
    message += `🤔 Maybe: ${maybeCount}\n`;
    message += `❌ Unavailable: ${unavailableCount}\n`;
    message += `📝 Total Voted: ${availabilities.length}\n\n`;

    // Group by status
    const availablePlayers = availabilities.filter(a => a.status === 'AVAILABLE');
    const reservePlayers = availabilities.filter(a => a.status === 'RESERVE');
    const maybePlayers = availabilities.filter(a => a.status === 'MAYBE');
    const unavailablePlayers = availabilities.filter(a => a.status === 'UNAVAILABLE');

    if (availablePlayers.length > 0) {
      message += `✅ *Available Players (${availablePlayers.length}):*\n`;
      availablePlayers.forEach((avail) => {
        const playerName = avail.player?.name || 'Unknown Player';
        const position = avail.player?.position ? ` - ${avail.player.position.replace('_', ' ')}` : '';
        message += `• ${playerName}${position}\n`;
      });
      message += `\n`;
    }

    if (reservePlayers.length > 0) {
      message += `🪑 *Reserve Players (${reservePlayers.length}):*\n`;
      reservePlayers.forEach((avail) => {
        const playerName = avail.player?.name || 'Unknown Player';
        const position = avail.player?.position ? ` - ${avail.player.position.replace('_', ' ')}` : '';
        message += `• ${playerName}${position}\n`;
      });
      message += `\n`;
    }

    if (maybePlayers.length > 0) {
      message += `🤔 *Maybe Players (${maybePlayers.length}):*\n`;
      maybePlayers.forEach((avail) => {
        const playerName = avail.player?.name || 'Unknown Player';
        const position = avail.player?.position ? ` - ${avail.player.position.replace('_', ' ')}` : '';
        message += `• ${playerName}${position}\n`;
      });
      message += `\n`;
    }

    if (unavailablePlayers.length > 0) {
      message += `❌ *Unavailable Players (${unavailablePlayers.length}):*\n`;
      unavailablePlayers.forEach((avail) => {
        const playerName = avail.player?.name || 'Unknown Player';
        const position = avail.player?.position ? ` - ${avail.player.position.replace('_', ' ')}` : '';
        message += `• ${playerName}${position}\n`;
      });
      message += `\n`;
    }

    // Add practice video links if available (only for practices)
    const videoLinks = type === 'practice' && entity && Array.isArray(entity.videoLinks) ? entity.videoLinks : [];
    if (videoLinks.length > 0) {
      message += `🎥 *Practice Videos:*\n`;
      videoLinks.forEach((link) => {
        message += `${link}\n`;
      });
      message += `\n`;
    }

    // Use api.whatsapp.com for better emoji support
    const whatsappUrl = getWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  const handleBack = () => {
    if (entity && type === 'practice' && entity.clubId) {
      // Navigate to the club's practice scheduling page
      navigate(soccerPath(`/clubs/${entity.clubId}/practices`));
    } else if (entity && type === 'match' && entity.clubId) {
      // Navigate to the club's match scheduling page
      navigate(soccerPath(`/clubs/${entity.clubId}/matches`));
    } else {
      // Fallback: try browser history, or go to clubs list
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(soccerPath('/clubs'));
      }
    }
  };

  // Check if coming from WhatsApp
  const urlParams = new URLSearchParams(window.location.search);
  const isFromWhatsApp = urlParams.get('source') === 'whatsapp';

  // Show loading if entity is not loaded yet
  if ((!entity && !authLoading) || (isMagicVote && tokenLoading)) {
    return <div className="loading">Loading...</div>;
  }

  const availabilityLocked =
    entity &&
    (type === 'practice' ? !canEditPracticeAvailability(entity) : entity.status === 'COMPLETED');

  return (
    <div className="availability-voting">
      {entity && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {!isFromWhatsApp && (
              <button
                onClick={handleBack}
                className="btn btn-secondary"
                style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
              >
                ← Back
              </button>
            )}
            <h2 style={{ flex: '1', minWidth: '200px' }}>{entity.title}</h2>
          </div>
          {entity.description && <p>{entity.description}</p>}
          <p><strong>Date:</strong> {new Date(type === 'match' ? entity.matchDate : entity.practiceDate).toLocaleString()}</p>
          {entity.location && <p><strong>Location:</strong> {entity.location}</p>}
          {type === 'practice' && entity.teamFormationId && (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <Link to={soccerPath(`/practices/${entity.id}/lineup`)} className="btn btn-primary">
                View team lineup
              </Link>
            </div>
          )}
          {type === 'practice' && canManagePracticeAvailability && canManageScrimmageStats(entity) && (
            <div style={{ marginTop: '0.75rem' }}>
              <Link to={soccerPath(`/practices/${entity.id}/scrimmage-stats`)} className="btn btn-secondary">
                📊 Update scrimmage stats
              </Link>
            </div>
          )}
          {type === 'practice' && (
            <PracticePlanSection
              practiceId={entity.id}
              practice={entity}
              practicePlan={entity.practicePlan}
              practicePlanStatus={entity.practicePlanStatus}
              canEdit={canEditPracticePlan}
              userId={user?.id}
              onUpdated={(updated) => setEntity(updated)}
            />
          )}
        </>
      )}

      {success && <div className="alert alert-success">{success}</div>}

      {isMagicVote && magicVoteContext && (
        <div className="alert alert-info" style={{
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '8px',
        }}>
          <strong>Personal vote link</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Voting as <strong>{magicVoteContext.playerName}</strong>. No login required.
          </p>
        </div>
      )}

      {isMagicVote && error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      
      {/* Show login/register section for unauthenticated users */}
      {!authLoading && !isAuthenticated && !isMagicVote && (
        <div className="alert alert-info" style={{ 
          marginBottom: '1rem', 
          padding: '1.5rem', 
          backgroundColor: '#e7f3ff', 
          border: '1px solid #b3d9ff', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
            Please login or join to vote
          </h3>
          <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
            You need to be logged in to vote your availability for this practice.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/login"
              onClick={() => {
                const currentUrl = window.location.pathname + window.location.search;
                sessionStorage.setItem('votingRedirectUrl', currentUrl);
              }}
              className="btn btn-primary"
              style={{ 
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                minWidth: '150px',
                textDecoration: 'none',
                display: 'inline-block',
                textAlign: 'center'
              }}
            >
              Login
            </Link>
            <Link
              to={entity?.clubId ? `/register?clubId=${encodeURIComponent(entity.clubId)}` : '/register'}
              onClick={() => {
                const currentUrl = window.location.pathname + window.location.search;
                sessionStorage.setItem('votingRedirectUrl', currentUrl);
                if (entity && entity.clubId) {
                  sessionStorage.setItem('votingClubId', entity.clubId);
                }
              }}
              className="btn btn-secondary"
              style={{ 
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                minWidth: '150px',
                backgroundColor: '#6c757d',
                color: 'white',
                textDecoration: 'none',
                display: 'inline-block',
                textAlign: 'center'
              }}
            >
              Join
            </Link>
          </div>
          <p style={{ margin: '1rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
            Already have an account? <strong>Login</strong>. New here? <strong>Join</strong> creates your account and player profile in one step.
          </p>
        </div>
      )}

      {/* Show pending approval message for authenticated users */}
      {isAuthenticated && membershipStatus === 'PENDING' && (
        <div className="alert alert-warning" style={{ 
          marginBottom: '1rem',
          padding: '1.5rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#856404' }}>
            Membership Pending Approval
          </h3>
          <p style={{ margin: '0', color: '#856404' }}>
            {clubName
              ? `Your membership request for ${clubName} is pending approval by the club admin. You will be able to vote once your membership is approved.`
              : 'Your membership request is pending approval by the club admin. You will be able to vote once your membership is approved.'}
          </p>
        </div>
      )}

      {/* Show error for authenticated users without player profile */}
      {isAuthenticated && error && error.includes('Player profile') && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
          <div style={{ marginTop: '1rem' }}>
            {hasPendingPlayer ? (
              <div className="alert alert-warning" style={{ 
                padding: '1rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                color: '#856404'
              }}>
                <strong>Player Registration Pending</strong>
                <p style={{ margin: '0.5rem 0 0 0' }}>
                  Your player registration is pending system admin approval. You will be able to vote once your registration is approved.
                </p>
              </div>
            ) : (
              <>
                <p>You need a player profile to vote. If you already have an account, complete your profile. New here? Use Join to sign up in one step.</p>
                <Link
                  to={completeProfilePath(entity?.clubId)}
                  onClick={() => {
                    const currentUrl = window.location.pathname + window.location.search;
                    const existingRedirect = sessionStorage.getItem('votingRedirectUrl');
                    if (!existingRedirect) {
                      sessionStorage.setItem('votingRedirectUrl', currentUrl);
                    }
                    if (entity && entity.clubId) {
                      sessionStorage.setItem('votingClubId', entity.clubId);
                    }
                  }}
                  className="btn btn-primary"
                  style={{ 
                    marginTop: '0.5rem',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                >
                  Complete player profile
                </Link>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Show other errors for authenticated users */}
      {isAuthenticated && error && !error.includes('Player profile') && membershipStatus !== 'PENDING' && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {isMagicVote && magicVoteContext && magicVoteContext.canVote && (
        <div className="voting-section">
          <h3>Your Availability</h3>
          {playerCapacity != null && playerCapacity > 0 && (
            <p className="capacity-notice" style={{ textAlign: 'center', color: '#555', marginBottom: '0.75rem' }}>
              {isAvailableFull
                ? `All ${playerCapacity} available spots are taken. Vote Reserve to join the backup list.`
                : `${availableCount} of ${playerCapacity} available spots filled.`}
            </p>
          )}
          <div
            className="vote-buttons"
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem',
              justifyContent: isMobile ? 'stretch' : 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={() => handleMagicVote('AVAILABLE')}
              className={`btn ${voteStatus === 'AVAILABLE' ? 'btn-success' : 'btn-secondary'}`}
              disabled={loading || !canSelectAvailable}
              title={!canSelectAvailable ? 'Practice is full — vote Reserve instead' : 'Vote available'}
              style={isMobile ? { fontSize: '1.2rem', padding: '1rem', width: '100%' } : {}}
            >
              ✅ Available
            </button>
            <button
              onClick={() => handleMagicVote('RESERVE')}
              className={`btn ${voteStatus === 'RESERVE' ? 'btn-info' : 'btn-secondary'}`}
              disabled={loading}
              style={isMobile ? { fontSize: '1.2rem', padding: '1rem', width: '100%' } : {}}
            >
              🪑 Reserve
            </button>
            <button
              onClick={() => handleMagicVote('MAYBE')}
              className={`btn ${voteStatus === 'MAYBE' ? 'btn-warning' : 'btn-secondary'}`}
              disabled={loading}
              style={isMobile ? { fontSize: '1.2rem', padding: '1rem', width: '100%' } : {}}
            >
              🤔 Maybe
            </button>
            <button
              onClick={() => handleMagicVote('UNAVAILABLE')}
              className={`btn ${voteStatus === 'UNAVAILABLE' ? 'btn-danger' : 'btn-secondary'}`}
              disabled={loading}
              style={isMobile ? { fontSize: '1.2rem', padding: '1rem', width: '100%' } : {}}
            >
              ❌ Unavailable
            </button>
          </div>
          {voteStatus && (
            <p className="current-vote">Your current vote: <strong>{voteStatus}</strong></p>
          )}
        </div>
      )}

      {isMagicVote && magicVoteContext && !magicVoteContext.canVote && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <strong>Practice session ended:</strong> Availability can no longer be changed.
        </div>
      )}

      {isMagicVote && entity && (
        <div className="availability-summary">
          <h3>Availability Summary</h3>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">Available:</span>
              <span className="stat-value">{availableCount}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Reserve:</span>
              <span className="stat-value">{reserveCount}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Maybe:</span>
              <span className="stat-value">{maybeCount}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Unavailable:</span>
              <span className="stat-value">{unavailableCount}</span>
            </div>
          </div>
        </div>
      )}

      {isAuthenticated && entity && !isMagicVote && (
        <>
          {type === 'practice' && !canEditPracticeAvailability(entity) && (
            <div className="alert alert-info" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px', color: '#0c5460' }}>
              <strong>Practice session ended:</strong> Availability can no longer be changed. You can still view the summary and teams.
            </div>
          )}
          {type === 'match' && entity.status === 'COMPLETED' && (
            <div className="alert alert-info" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px', color: '#0c5460' }}>
              <strong>Match completed:</strong> Availability cannot be edited, but you can still view the summary.
            </div>
          )}
          <div className="voting-section">
            <h3>Your Availability</h3>
            {type === 'practice' && playerCapacity != null && playerCapacity > 0 && (
              <p className="capacity-notice" style={{ textAlign: 'center', color: '#555', marginBottom: '0.75rem' }}>
                {isAvailableFull
                  ? `All ${playerCapacity} available spots are taken. Vote Reserve to join the backup list.`
                  : `${availableCount} of ${playerCapacity} available spots filled.`}
              </p>
            )}
            <div 
              className="vote-buttons" 
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '1rem',
                justifyContent: isMobile ? 'stretch' : 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={() => handleVote('AVAILABLE')}
                className={`btn ${voteStatus === 'AVAILABLE' ? 'btn-success' : 'btn-secondary'}`}
                disabled={loading || availabilityLocked || !canSelectAvailable}
                title={!canSelectAvailable ? 'Practice is full — vote Reserve instead' : 'Vote available'}
                style={isMobile ? { 
                  fontSize: '1.2rem', 
                  padding: '1rem',
                  width: '100%'
                } : {}}
              >
                ✅ Available
              </button>
              {type === 'practice' && (
                <button
                  onClick={() => handleVote('RESERVE')}
                  className={`btn ${voteStatus === 'RESERVE' ? 'btn-info' : 'btn-secondary'}`}
                  disabled={loading || availabilityLocked}
                  style={isMobile ? { 
                    fontSize: '1.2rem', 
                    padding: '1rem',
                    width: '100%'
                  } : {}}
                >
                  🪑 Reserve
                </button>
              )}
              <button
                onClick={() => handleVote('MAYBE')}
                className={`btn ${voteStatus === 'MAYBE' ? 'btn-warning' : 'btn-secondary'}`}
                disabled={loading || availabilityLocked}
                style={isMobile ? { 
                  fontSize: '1.2rem', 
                  padding: '1rem',
                  width: '100%'
                } : {}}
              >
                🤔 Maybe
              </button>
              <button
                onClick={() => handleVote('UNAVAILABLE')}
                className={`btn ${voteStatus === 'UNAVAILABLE' ? 'btn-danger' : 'btn-secondary'}`}
                disabled={loading || availabilityLocked}
                style={isMobile ? { 
                  fontSize: '1.2rem', 
                  padding: '1rem',
                  width: '100%'
                } : {}}
              >
                ❌ Unavailable
              </button>
            </div>
            {voteStatus && (
              <p className="current-vote">Your current vote: <strong>{voteStatus}</strong></p>
            )}
          </div>

          <div className="availability-summary">
            <h3>Availability Summary</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Available:</span>
                <span className="stat-value">{availableCount}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Reserve:</span>
                <span className="stat-value">{reserveCount}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Maybe:</span>
                <span className="stat-value">{maybeCount}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Unavailable:</span>
                <span className="stat-value">{unavailableCount}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Practice availability management — club admin or practice coordinator */}
      {type === 'practice' && canManagePracticeAvailability && entity && !availabilityLocked && (
        <div className="admin-section" style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Manage Practice Availability</h3>
            <button
              type="button"
              onClick={sharePublicPracticePageViaWhatsApp}
              className="btn"
              disabled={loading}
              style={{
                backgroundColor: '#25D366',
                color: 'white',
                border: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              📋 Share Public Practice Page
            </button>
          </div>
          
          {/* Add Players Section */}
          <div style={{ marginBottom: '2rem' }}>
            <button
              onClick={() => {
                setShowAddPlayerForm(!showAddPlayerForm);
                if (showAddPlayerForm) {
                  setSelectedPlayerIds([]);
                  setAddPlayerSearch('');
                }
              }}
              className="btn btn-primary"
              style={{ marginBottom: '1rem' }}
            >
              {showAddPlayerForm ? 'Cancel' : 'Add Players to Practice'}
            </button>
            
            {showAddPlayerForm && (
              <div className="add-players-panel">
                <div className="add-players-panel__toolbar">
                  <label>
                    Select players ({selectedPlayerIds.length} selected
                    {addPlayerSearch.trim() && filteredPlayersNotYetAdded.length !== playersNotYetAdded.length
                      ? ` · ${filteredPlayersNotYetAdded.length} shown`
                      : ''}
                    )
                  </label>
                  <div className="add-players-panel__toolbar-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleSelectAllAddPlayers}
                      disabled={loading || filteredPlayersNotYetAdded.length === 0}
                    >
                      {filteredPlayersNotYetAdded.length > 0 &&
                      filteredPlayersNotYetAdded.every((p) =>
                        selectedPlayerIds.includes(p.id)
                      )
                        ? 'Deselect all'
                        : 'Select all'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleClearAddPlayerSelection}
                      disabled={loading || selectedPlayerIds.length === 0}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {playersNotYetAdded.length > 0 && (
                  <input
                    type="search"
                    className="player-search-input"
                    placeholder="Search players by name…"
                    value={addPlayerSearch}
                    onChange={(e) => setAddPlayerSearch(e.target.value)}
                    disabled={loading}
                    aria-label="Search players to add"
                  />
                )}

                {playersNotYetAdded.length === 0 ? (
                  <p className="add-players-panel__empty">
                    All club players are already on this practice list.
                  </p>
                ) : filteredPlayersNotYetAdded.length === 0 ? (
                  <p className="add-players-panel__empty">
                    No players match &ldquo;{addPlayerSearch.trim()}&rdquo;.
                  </p>
                ) : (
                  <div className="add-players-panel__list">
                    {filteredPlayersNotYetAdded.map((player) => (
                      <label key={player.id} className="add-players-panel__item">
                        <input
                          type="checkbox"
                          checked={selectedPlayerIds.includes(player.id)}
                          onChange={() => handleToggleAddPlayer(player.id)}
                          disabled={loading}
                        />
                        <span>
                          {player.name}
                          {player.position && (
                            <span className="add-players-panel__position">
                              {' '}
                              ({player.position.replace('_', ' ')})
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label htmlFor="bulk-add-status">Status for selected players</label>
                  <select
                    id="bulk-add-status"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', padding: '0.5rem' }}
                  >
                    <option value="AVAILABLE">Available</option>
                    <option value="RESERVE">Reserve</option>
                    <option value="MAYBE">Maybe</option>
                    <option value="UNAVAILABLE">Unavailable</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={handleAddSelectedPlayers}
                  className="btn btn-primary"
                  disabled={loading || selectedPlayerIds.length === 0}
                >
                  {loading
                    ? 'Adding…'
                    : `Add selected (${selectedPlayerIds.length})`}
                </button>
              </div>
            )}
          </div>

          {/* Players Who Voted Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h4 style={{ margin: 0 }}>
                Players Who Voted ({availabilities.length}
                {votedPlayerSearch.trim() && filteredAvailabilities.length !== availabilities.length
                  ? ` · ${filteredAvailabilities.length} shown`
                  : ''}
                )
              </h4>
              {availabilities.length > 0 && (
                <button
                  onClick={shareVotedPlayersViaWhatsApp}
                  className="btn"
                  style={{ 
                    backgroundColor: '#25D366', 
                    color: 'white', 
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📱 Share via WhatsApp
                </button>
              )}
            </div>
            {availabilities.length > 0 && (
              <input
                type="search"
                className="player-search-input"
                placeholder="Search players by name…"
                value={votedPlayerSearch}
                onChange={(e) => setVotedPlayerSearch(e.target.value)}
                disabled={loading}
                aria-label="Search players who voted"
              />
            )}
            {availabilities.length === 0 ? (
              <p>No players have voted yet.</p>
            ) : filteredAvailabilities.length === 0 ? (
              <p className="add-players-panel__empty">
                No players match &ldquo;{votedPlayerSearch.trim()}&rdquo;.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
                {filteredAvailabilities.map((avail) => (
                  <div
                    key={avail.id}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}
                  >
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <strong>{avail.player?.name || 'Unknown Player'}</strong>
                      {avail.player?.position && (
                        <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                          ({avail.player.position})
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      flexWrap: 'wrap',
                      flex: '1',
                      justifyContent: 'flex-end'
                    }}>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          backgroundColor:
                            avail.status === 'AVAILABLE' ? '#d4edda' :
                            avail.status === 'RESERVE' ? '#cfe2ff' :
                            avail.status === 'UNAVAILABLE' ? '#f8d7da' :
                            '#fff3cd',
                          color:
                            avail.status === 'AVAILABLE' ? '#155724' :
                            avail.status === 'RESERVE' ? '#084298' :
                            avail.status === 'UNAVAILABLE' ? '#721c24' :
                            '#856404',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {avail.status}
                      </span>
                      {canManagePracticeAvailability && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.25rem', 
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          <select
                            value={avail.status}
                            onChange={(e) => handleUpdatePlayerVote(avail.playerId, e.target.value)}
                            disabled={loading || availabilityLocked}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.85rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              cursor: loading || availabilityLocked ? 'not-allowed' : 'pointer',
                              minWidth: '120px'
                            }}
                            title="Update vote status"
                          >
                            <option value="AVAILABLE">Available</option>
                            <option value="RESERVE">Reserve</option>
                            <option value="MAYBE">Maybe</option>
                            <option value="UNAVAILABLE">Unavailable</option>
                          </select>
                          <button
                            onClick={() => handleRemovePlayer(avail.playerId, avail.player?.name)}
                            disabled={loading || availabilityLocked}
                            className="btn btn-danger"
                            style={{
                              fontSize: '0.85rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Remove from practice"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityVoting;
